import { Button, cn } from "@babybjorn/ui";
import type { StoryblokRichTextNode } from "@storyblok/react";
import { useTranslation } from "gatsby-plugin-react-i18next";
import { useMemo, type ReactElement } from "react";
import ImageAndText from "../../components/ImageAndText";
import * as ImageStyles from "../../components/ImageAndText/ImageAndText.module.scss";
import Content from "../../components/ImageAndText/components/Content";
import * as ContentStyles from "../../components/ImageAndText/components/Content/Content.module.scss";
import Header from "../../components/ImageAndText/components/Header";
import MiniCard from "../../components/ImageAndText/components/MiniCard";
import ProductCard from "../../components/ProductCard";
import type { ProductCardProps } from "../../components/ProductCard/ProductCard";
import VimeoPlayer from "../../components/VimeoPlayer/VimeoPlayer";
import GridItem from "../../components/general/GridItem";
import Hotspots from "../../components/general/Hotspots";
import Icon, { isValidIcon } from "../../components/general/Icon/Icon";
import ImageContainer from "../../components/general/ImageContainers";
import { validImageRatio } from "../../components/general/ImageContainers/ImageContainer";
import Link from "../../components/general/Link";
import Tag from "../../components/general/Tag";
import { useAppState } from "../../context/app/context";
import { useCartService } from "../../hooks/use-cart-service";
import { useIsInPreviewEditor } from "../../hooks/use-is-in-preview-editor";
import { mapStoryblokVariantToProductCard } from "../../utils/product-card";
import { mapHotspotsToHotspotsProps } from "../../utils/storyblok/product-variant";
import { getUrlFromStoryblokLink } from "../../utils/storyblok/urls";
import { getValidType } from "../../utils/type-guards/valid";

const ImageAndTextModule = ({
	_uid,
	imageFirst,
	textRight,
	tag,
	label,
	headline,
	content,
	image_ratio,
	image,
	buttons,
	product: productId,
	storyblok_product_variant_page,
	vimeo,
	hotspots,
}: Queries.ImageAndTextModuleFragment) => {
	const { addItemsToCart } = useCartService();
	const { t } = useTranslation();
	const { siteConfig, userCountry } = useAppState();
	const buttonText = t("Add to cart");

	const isInPreviewEditor = useIsInPreviewEditor();

	const vimeoPoster = vimeo?.vimeo_oembed?.response?.thumbnail_url;
	const vimeoID = vimeo?.vimeo_oembed?.response?.video_id;
	const vimeoWidth = vimeo?.vimeo_oembed?.response?.width;
	const vimeoHeight = vimeo?.vimeo_oembed?.response?.height;

	const productCardProps = useMemo(() => {
		return isInPreviewEditor && productId
			? placeholderProduct
			: mapStoryblokVariantToProductCard({
					variantPage: storyblok_product_variant_page,
					addItemsToCart,
					buttonText,
					currency: siteConfig.currency,
					country: userCountry,
					buttonType: "standard",
				});
	}, [
		isInPreviewEditor,
		productId,
		storyblok_product_variant_page,
		addItemsToCart,
		buttonText,
		siteConfig.currency,
		userCountry,
	]);

	if (productCardProps) {
		const image = productCardProps.originalImages?.[0];
		const altImage = productCardProps.originalImages?.[1];
		productCardProps.image = (
			<ImageContainer
				alt={image?.alt ?? productCardProps.name}
				ratio="portrait"
				objectFit={image?.addPadding ? "contain" : "cover"}
				imgSrc={image?.src}
				addPadding={image?.addPadding ?? undefined}
				paddingVariant="productCard"
				sizes="(max-width: 768px) 80vw, 50vw"
				hidePlaceholder
			/>
		);

		if (altImage)
			productCardProps.altImage = (
				<ImageContainer
					alt={altImage.alt ?? productCardProps.name}
					ratio="portrait"
					objectFit={altImage.addPadding ? "contain" : "cover"}
					imgSrc={altImage.src}
					addPadding={altImage.addPadding ?? undefined}
					paddingVariant="productCard"
					sizes="(max-width: 768px) 80vw, 50vw"
					hidePlaceholder
				/>
			);
	}
	const firstButton = buttons?.[0];
	const secondButton = buttons?.[1];

	return (
		<GridItem>
			<ImageAndText
				imageFirst={imageFirst ?? false}
				textRight={textRight ?? false}
			>
				<Header headline={headline ?? ""} label={label ?? ""}>
					{tag && <Tag label={tag} />}
				</Header>
				{image?.filename || vimeoID ? (
					<div
						className={cn({
							[ImageStyles.doubleImageContainer]: productCardProps?.name,
						})}
					>
						{image?.filename && !vimeoID && (
							<Hotspots
								name="Image and text module"
								hotspots={mapHotspotsToHotspotsProps(hotspots)}
							>
								<ImageContainer
									alt={image.alt ?? undefined}
									ratio={getValidType(image_ratio, validImageRatio)}
									imgSrc={image.filename}
									objectFit="cover"
								/>
							</Hotspots>
						)}
						{vimeoID && (
							<div>
								<VimeoPlayer
									videoID={vimeoID}
									playerID={_uid + vimeoID.toString()}
									posterUrl={vimeoPoster ?? ""}
									width={vimeoWidth ?? undefined}
									height={vimeoHeight ?? undefined}
									title={vimeo.vimeo_oembed.response.title ?? "Vimeo Video"}
								/>
							</div>
						)}
						{productCardProps && (
							<Link
								href={productCardProps.url}
								aria-label={`${t("More about")}: ${productCardProps.name}`}
							>
								<MiniCard
									productName={productCardProps.name}
									fabric={productCardProps.fabric}
									color={productCardProps.color}
								>
									{productCardProps.altImage}
								</MiniCard>
							</Link>
						)}
					</div>
				) : (
					<>
						{productCardProps && (
							<ProductCard
								{...productCardProps}
								linkComponent={(children) => (
									<Link
										href={productCardProps.url}
										aria-label={`${t("More about")}: ${productCardProps.name}`}
									>
										{children}
									</Link>
								)}
								inModule
							/>
						)}
					</>
				)}
				<Content content={content as StoryblokRichTextNode<ReactElement>}>
					{buttons && (
						<div
							className={cn({
								[ContentStyles.buttonContainer]: buttons.length > 1,
							})}
						>
							{firstButton && (
								<Button asChild variant="secondary">
									<Link
										href={getUrlFromStoryblokLink(firstButton.link)}
										target={firstButton.link?.target ?? undefined}
										anchor={firstButton.link?.anchor ?? undefined}
									>
										{isValidIcon(firstButton.icon) && (
											<Icon icon={firstButton.icon} />
										)}
										{firstButton.label}
									</Link>
								</Button>
							)}
							{secondButton && (
								<Button asChild variant="ghost">
									<Link
										href={getUrlFromStoryblokLink(secondButton.link)}
										target={secondButton.link?.target ?? undefined}
										anchor={secondButton.link?.anchor ?? undefined}
									>
										<Icon
											icon={
												isValidIcon(secondButton.icon)
													? secondButton.icon
													: "arrow-right"
											}
										/>
										{secondButton.label}
									</Link>
								</Button>
							)}
						</div>
					)}
				</Content>
			</ImageAndText>
		</GridItem>
	);
};

const placeholderProduct: ProductCardProps = {
	name: "[PRODUCT NAME]",
	rawPrice: {
		centAmount: 9995,
		currencyCode: "USD",
		fractionDigits: 2,
		type: "centPrecision",
	},
	image: (
		<ImageContainer
			alt="Placeholder product image"
			ratio="portrait"
			objectFit="cover"
			imgSrc="https://placehold.jp/30/666666/ffffff/500x500.png?text=Product+image"
		/>
	),
	altImage: (
		<ImageContainer
			alt="Placeholder product image"
			ratio="portrait"
			objectFit="cover"
			imgSrc="https://placehold.jp/30/666666/ffffff/500x500.png?text=Product+image"
		/>
	),
	url: " ",
	fabric: "[FABRIC]",
	color: "[COLOR]",
	children: <></>,
	linkComponent: (children) => <Link href="">{children}</Link>,
};

export default ImageAndTextModule;
